import qs from 'qs'
import React, { useState, useEffect, useCallback } from 'react'
import withStyles from '@mui/styles/withStyles'

import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import FormLabel from '@mui/material/FormLabel'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import Radio from '@mui/material/Radio'
import Link from '@mui/material/Link'

import * as medicRegistrationHttp from '../http/medicregistration'
import {
  IDADE_MAXIMA, IDADE_MINIMA, cpfValido, erroCpf, filtrarLetras, formatarCelular,
  formatarCep, formatarCpf, idadeAceita, limitesNascimento, normalizarEspacos,
  soLetras, somenteDigitos,
} from '../cadastroMedico'
import Loading from '../components/Loading'

const UFS = ('AC AL AP AM BA CE DF ES GO MA MT MS MG PA PB PR PE PI RJ RN RS ' +
  'RO RR SC SP SE TO').split(' ')

const styles = theme => ({
  appBar: { zIndex: theme.zIndex.drawer + 1 },
  title: { flexGrow: 1, paddingLeft: 24 },
  // O aviso de cookies e fixo no rodape e cobria o botao de enviar.
  // Quem chega aqui vem por link, quase sempre em janela nova, entao
  // ve o aviso SEMPRE -- diferente do sistema logado, onde aparece uma
  // vez so. A folga embaixo garante que o formulario nao fique sob ele.
  content: {
    maxWidth: 760, margin: '32px auto', padding: theme.spacing(3),
    paddingBottom: 140
  },
  secao: { marginTop: theme.spacing(3), fontWeight: 'bold' }
})

function readUrlQuery(search) {
  return qs.parse(search, { ignoreQueryPrefix: true })
}

const CAMPOS_VAZIOS = {
  first_name: '', last_name: '', birth_date: '', address: '', city: '',
  state: '', zip_code: '', report_name: '', crm: '', crm_state: '', rqe: '',
  email: '', phone: '', computer_type: '', hwid: '', soluti_username: ''
}

// Campos que guardam SÓ dígitos no estado (a máscara é aplicada na exibição).
// Dados de cadastros antigos podem vir com pontuação; normalizamos ao carregar.
const CAMPOS_NUMERICOS = { cpf: 11, zip_code: 8, phone: 11, crm: 10, rqe: 10 }

function MedicRegistration(props) {
  const { classes } = props
  const query = readUrlQuery(props.location ? props.location.search : '')
  const token = query.token

  // etapas: lgpd -> cpf -> dados -> enviado
  const [etapa, setEtapa] = useState('lgpd')
  const [carregando, setCarregando] = useState(true)
  const [erroToken, setErroToken] = useState(null)
  const [clinicas, setClinicas] = useState([])
  const [erro, setErro] = useState(null)

  const [cpf, setCpf] = useState('')
  const [jaCadastrado, setJaCadastrado] = useState(false)
  const [form, setForm] = useState(CAMPOS_VAZIOS)
  const [assinatura, setAssinatura] = useState(null)

  useEffect(() => {
    const validar = async () => {
      if (!token) {
        setErroToken('Link inválido: token não informado.')
        setCarregando(false)
        return
      }
      const { result, data } = await medicRegistrationHttp.validateInvite(token)
      if (result && data.valid) {
        setClinicas(data.clinics || [])
        setForm(f => ({ ...f, email: data.email || '' }))
      } else {
        setErroToken(
          (data && data.message) ||
          'Link inválido ou expirado. Solicite um novo à clínica.'
        )
      }
      setCarregando(false)
    }
    validar()
  }, [token])

  const alterar = useCallback(campo => evento => {
    const valor = evento.target.value
    setForm(anterior => ({ ...anterior, [campo]: valor }))
  }, [])

  // "Só números": descarta na digitação qualquer outro caractere.
  const alterarDigitos = useCallback((campo, maximo) => evento => {
    const valor = somenteDigitos(evento.target.value, maximo)
    setForm(anterior => ({ ...anterior, [campo]: valor }))
  }, [])

  // "Só letras": descarta dígitos e símbolos na digitação, mas mantém espaço,
  // hífen e apóstrofo, que aparecem em nomes e cidades.
  const alterarLetras = useCallback(campo => evento => {
    const valor = filtrarLetras(evento.target.value)
    setForm(anterior => ({ ...anterior, [campo]: valor }))
  }, [])

  // Passo 1 -> 2: sem o consentimento o processo encerra aqui
  const recusarLgpd = useCallback(() => {
    setErroToken(
      'Sem a autorização para coleta de dados não é possível prosseguir com ' +
      'o cadastro. Se mudar de ideia, use novamente o link recebido.'
    )
  }, [])

  // Passo 2 -> 3: identifica pelo CPF
  const identificar = useCallback(async () => {
    setErro(null)
    const apenasDigitos = somenteDigitos(cpf)
    if (apenasDigitos.length !== 11) {
      setErro('Informe os 11 números do CPF.')
      return
    }
    // Barrar aqui evita gastar uma das tentativas limitadas do convite com um
    // CPF que nem é matematicamente possível.
    if (!cpfValido(apenasDigitos)) {
      setErro('CPF inválido. Confira os números.')
      return
    }
    setCarregando(true)
    const { result, data } = await medicRegistrationHttp.identifyByCpf(
      token, apenasDigitos
    )
    setCarregando(false)

    if (!result) {
      setErro((data && data.message) || 'Não foi possível verificar o CPF.')
      return
    }

    if (data.existing) {
      setJaCadastrado(true)
      setForm(anterior => ({
        ...anterior,
        ...Object.keys(data.data).reduce((acumulado, chave) => {
          const valor = data.data[chave] === null ? '' : data.data[chave]
          acumulado[chave] = chave in CAMPOS_NUMERICOS
            ? somenteDigitos(valor, CAMPOS_NUMERICOS[chave])
            : valor
          return acumulado
        }, {})
      }))
    } else {
      setJaCadastrado(false)
      setForm(anterior => ({ ...anterior, cpf: apenasDigitos }))
    }
    setForm(anterior => ({ ...anterior, cpf: apenasDigitos }))
    setEtapa('dados')
  }, [cpf, token])

  const enviar = useCallback(async evento => {
    evento.preventDefault()
    setErro(null)

    // Todos os campos do formulario sao obrigatorios, EXCETO 'rqe' (nem todo
    // medico possui) e 'soluti_username' (nem todo medico usa assinatura
    // Soluti). O 'hwid' nao aparece aqui: so existe apos instalar o
    // ImagiPACS Uni, e e informado por link proprio.
    const obrigatorios = [
      ['first_name', 'Nome'], ['last_name', 'Sobrenome'],
      ['birth_date', 'Data de nascimento'],
      ['address', 'Endereço'], ['city', 'Cidade'], ['state', 'Estado'],
      ['zip_code', 'CEP'], ['report_name', 'Nome a ser impresso no laudo'],
      ['crm', 'CRM'], ['crm_state', 'Estado de emissão do CRM'],
      ['email', 'E-mail'], ['phone', 'Celular'],
      ['computer_type', 'Computador que será usado']
    ]
    const faltando = obrigatorios.filter(([campo]) => !String(form[campo] || '').trim())
    if (faltando.length) {
      setErro('Preencha: ' + faltando.map(([, rotulo]) => rotulo).join(', ') + '.')
      return
    }

    // Regras de formato (as mesmas valem no servidor). Reunimos todos os
    // problemas numa mensagem só, para não obrigar a corrigir um por envio.
    const problemas = []
    if (!soLetras(form.first_name)) problemas.push('Nome deve conter apenas letras')
    if (!soLetras(form.last_name)) problemas.push('Sobrenome deve conter apenas letras')
    if (!soLetras(form.city)) problemas.push('Cidade deve conter apenas letras')
    if (!idadeAceita(form.birth_date)) {
      problemas.push(`Data de nascimento: é preciso ter entre ${IDADE_MINIMA} e ${IDADE_MAXIMA} anos`)
    }
    if (somenteDigitos(form.zip_code).length !== 8) problemas.push('CEP deve ter 8 números')
    if (somenteDigitos(form.phone).length !== 11) problemas.push('Celular deve ter 11 números, com DDD')
    if (problemas.length) {
      setErro(problemas.join('. ') + '.')
      return
    }
    if (!jaCadastrado && !assinatura) {
      setErro('Anexe a imagem da sua assinatura.')
      return
    }

    setCarregando(true)
    const { result, data } = await medicRegistrationHttp.submitRegistration(
      token,
      {
        ...form,
        cpf: somenteDigitos(form.cpf),
        first_name: normalizarEspacos(form.first_name),
        last_name: normalizarEspacos(form.last_name),
        city: normalizarEspacos(form.city),
      },
      assinatura
    )
    setCarregando(false)

    if (result) {
      setEtapa('enviado')
    } else {
      setErro((data && data.message) || 'Não foi possível enviar o cadastro.')
    }
  }, [form, assinatura, token, jaCadastrado])

  const cabecalho = (
    <AppBar position="static" className={classes.appBar} style={{ background: '#58b0c0' }}>
      <Toolbar>
        <Typography variant="h6" className={classes.title}>
          Cadastro de Médico - IMAGIRIS
        </Typography>
      </Toolbar>
    </AppBar>
  )

  if (carregando) {
    return <React.Fragment>{cabecalho}<Loading /></React.Fragment>
  }

  if (erroToken) {
    return (
      <React.Fragment>{cabecalho}
        <Paper className={classes.content}>
          <Alert severity="warning">{erroToken}</Alert>
        </Paper>
      </React.Fragment>
    )
  }

  if (etapa === 'enviado') {
    return (
      <React.Fragment>{cabecalho}
        <Paper className={classes.content}>
          <Alert severity="success">
            Cadastro enviado com sucesso! Você receberá um e-mail assim que o
            acesso for liberado, com as instruções para definir sua senha.
          </Alert>
        </Paper>
      </React.Fragment>
    )
  }

  // ------------------------------------------------------------------ LGPD
  if (etapa === 'lgpd') {
    return (
      <React.Fragment>{cabecalho}
        <Paper className={classes.content}>
          <Typography variant="h6" gutterBottom>Autorização</Typography>
          <Typography paragraph>
            De acordo com a Lei Geral de Proteção de Dados nº 13.709, de 14 de
            agosto de 2018, o Sr(a) autoriza a coleta de dados pessoais?
          </Typography>
          <Box mt={3}>
            <Button
              variant="contained"
              style={{ background: '#58b0c0', color: 'white', marginRight: 12 }}
              onClick={() => setEtapa('cpf')}
            >
              Sim, autorizo
            </Button>
            <Button variant="outlined" onClick={recusarLgpd}>Não autorizo</Button>
          </Box>
        </Paper>
      </React.Fragment>
    )
  }

  // ------------------------------------------------------------------- CPF
  if (etapa === 'cpf') {
    return (
      <React.Fragment>{cabecalho}
        <Paper className={classes.content}>
          <Typography variant="h6" gutterBottom>Identificação</Typography>
          <Typography paragraph color="textSecondary">
            Informe seu CPF. Se você já tiver cadastro conosco, os dados virão
            preenchidos para conferência.
          </Typography>
          {erro && <Box mb={2}><Alert severity="error">{erro}</Alert></Box>}
          <TextField
            label="CPF"
            placeholder="000.000.000-00"
            value={formatarCpf(cpf)}
            onChange={e => setCpf(somenteDigitos(e.target.value, 11))}
            onKeyDown={e => { if (e.key === 'Enter') identificar() }}
            inputProps={{ inputMode: 'numeric', maxLength: 14 }}
            error={Boolean(erroCpf(cpf))}
            helperText={erroCpf(cpf) || 'Digite apenas os números; a pontuação é automática.'}
            fullWidth
            autoFocus
          />
          <Box mt={3}>
            <Button
              variant="contained"
              style={{ background: '#58b0c0', color: 'white' }}
              onClick={identificar}
            >
              Continuar
            </Button>
          </Box>
        </Paper>
      </React.Fragment>
    )
  }

  // ----------------------------------------------------------------- Dados
  return (
    <React.Fragment>{cabecalho}
      <Paper className={classes.content}>
        <Typography variant="h6" gutterBottom>
          {jaCadastrado ? 'Confira seus dados' : 'Preencha seus dados'}
        </Typography>

        {jaCadastrado && (
          <Box mb={2}>
            <Alert severity="info">
              Encontramos seu cadastro. Confira os dados e corrija o que for
              necessário — CPF e e-mail não podem ser alterados por aqui.
            </Alert>
          </Box>
        )}

        {clinicas.length > 0 && (
          <Box mb={2}>
            <Typography variant="body2" color="textSecondary">
              Clínica/hospital em que usará o sistema:{' '}
              <strong>{clinicas.map(c => c.name).join(', ')}</strong>
            </Typography>
          </Box>
        )}

        {erro && <Box mb={2}><Alert severity="error">{erro}</Alert></Box>}

        <form onSubmit={enviar}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="CPF" value={formatarCpf(form.cpf)} fullWidth disabled />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Data de nascimento" type="date"
                value={form.birth_date || ''} onChange={alterar('birth_date')}
                InputLabelProps={{ shrink: true }} fullWidth required
                inputProps={limitesNascimento()}
                error={Boolean(form.birth_date) && !idadeAceita(form.birth_date)}
                helperText={
                  form.birth_date && !idadeAceita(form.birth_date)
                    ? `É preciso ter entre ${IDADE_MINIMA} e ${IDADE_MAXIMA} anos.`
                    : ''
                }
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField label="Nome" value={form.first_name}
                onChange={alterarLetras('first_name')} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Sobrenome" value={form.last_name}
                onChange={alterarLetras('last_name')} fullWidth required />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Endereço residencial / nº / apto / bairro"
                value={form.address} onChange={alterar('address')} fullWidth required
              />
            </Grid>
            <Grid item xs={12} sm={5}>
              <TextField label="Cidade" value={form.city}
                onChange={alterarLetras('city')} fullWidth required />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField select label="Estado" value={form.state}
                onChange={alterar('state')} fullWidth required>
                {UFS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField label="CEP" placeholder="00000-000"
                value={formatarCep(form.zip_code)}
                onChange={alterarDigitos('zip_code', 8)}
                inputProps={{ inputMode: 'numeric', maxLength: 9 }}
                fullWidth required />
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.secao} variant="body2">
                Dados profissionais
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Nome a ser impresso no laudo"
                value={form.report_name} onChange={alterar('report_name')}
                fullWidth required
              />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField label="CRM" value={form.crm}
                onChange={alterarDigitos('crm', 10)}
                inputProps={{ inputMode: 'numeric' }}
                helperText="Apenas números" fullWidth required />
            </Grid>
            <Grid item xs={6} sm={4}>
              <TextField select label="Estado do CRM" value={form.crm_state}
                onChange={alterar('crm_state')} fullWidth required>
                {UFS.map(uf => <MenuItem key={uf} value={uf}>{uf}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="RQE"
                value={form.rqe} onChange={alterarDigitos('rqe', 10)}
                inputProps={{ inputMode: 'numeric' }}
                helperText="Apenas números. Em branco se não possui."
                fullWidth
              />
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.secao} variant="body2">
                Contato e acesso
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {/* Não editável: vale o e-mail do convite (ou do cadastro
                  existente). O servidor ignora qualquer e-mail enviado. */}
              <TextField
                label="E-mail para uso no sistema" type="email"
                value={form.email} fullWidth disabled
                helperText="Para alterar o e-mail, fale com a clínica ou o suporte."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Celular com DDD" placeholder="(00) 00000-0000"
                value={formatarCelular(form.phone)}
                onChange={alterarDigitos('phone', 11)}
                inputProps={{ inputMode: 'tel', maxLength: 15 }}
                fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Usuário Soluti (assinatura digital)"
                value={form.soluti_username} onChange={alterar('soluti_username')}
                fullWidth />
            </Grid>

            <Grid item xs={12}>
              <FormControl required>
                <FormLabel>Computador que será usado</FormLabel>
                <RadioGroup row value={form.computer_type}
                  onChange={alterar('computer_type')}>
                  <FormControlLabel value="windows" control={<Radio />} label="Windows" />
                  <FormControlLabel value="apple" control={<Radio />} label="Apple" />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Typography className={classes.secao} variant="body2">
                Assinatura
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Anexe uma imagem (JPG ou PNG) da sua assinatura feita em folha
                completamente branca, sem nada ao redor e sem qualquer
                identificação.
              </Typography>
              <Box mb={2}>
                <Typography variant="caption" color="textSecondary" display="block">
                  Exemplo de imagem adequada:
                </Typography>
                {/* Se o arquivo não estiver publicado, some em vez de exibir
                    ícone de imagem quebrada. */}
                <img
                  src={`${process.env.PUBLIC_URL}/assinatura-exemplo.png`}
                  alt="Exemplo de assinatura em fundo branco"
                  style={{ maxWidth: 220, border: '1px solid #ddd', background: '#fff', padding: 8 }}
                  onError={e => { e.currentTarget.parentNode.style.display = 'none' }}
                />
              </Box>
              <input type="file" accept="image/*"
                onChange={e => setAssinatura(e.target.files[0] || null)} />
              {jaCadastrado && (
                <Typography variant="caption" display="block" color="textSecondary">
                  Opcional: só envie se quiser substituir a assinatura atual.
                </Typography>
              )}
            </Grid>
          </Grid>

          <Box mt={4}>
            <Typography variant="caption" color="textSecondary" paragraph>
              Ao clicar em ENVIAR, você concorda com a nossa{' '}
              <Link href="https://www.imagiris.com.br/privacy/" target="_blank"
                rel="noopener noreferrer">Política de Privacidade</Link>{' '}
              e com os requisitos da LGPD (Lei nº 13.709/2018).
            </Typography>
            <Button type="submit" variant="contained"
              style={{ background: '#58b0c0', color: 'white' }}>
              Enviar cadastro
            </Button>
          </Box>
        </form>
      </Paper>
    </React.Fragment>
  )
}

export default withStyles(styles)(MedicRegistration)
